// src/components/admin/questions/QuestionFormTab.tsx
// ✅ Editor rico (QuestionRichEditor) tanto no enunciado quanto na resolução
// ✅ Categorias reconstruídas ao editar (chips + selects hierárquicos)
// ✅ Timing seguro: pendingCatIds / pendingLegacyCat resolvem race condition
// ✅ ESLint no-loop-fix: funções puras no escopo do módulo
// ✅ QuestionNavigatorBar: Nova Questão | Editar em Série | Importar JSON

import React, { useState, useEffect, useRef } from 'react';
import {
    Plus, Trash2, X, Save, ChevronDown, ChevronUp,
    CheckCircle, AlertCircle, Loader2, ClipboardPaste,
    ChevronLeft, ChevronRight, FileJson, Upload,
    Copy, RefreshCw, Download, Check, FlaskConical,
    BookLock,
} from 'lucide-react';
import { questionService } from '../../../services/questionService';
import { CategoryNode } from '../../../types/question.types';
import { ImageZoomModal } from '../../CardEditor/ImageZoomModal';
import QuestionRichEditor from './QuestionRichEditor';
import PasteAlternativesModal from './PasteAlternativesModal';
import './QuestionFormTab.css';

/* ─── tipos locais ──────────────────────────────────────────── */
type EditorMode = 'create' | 'edit' | 'import' | 'revisor' | 'prova' | 'simulado';
type QuestionStatus = 'rascunho' | 'publicado' | 'analise' | 'prova' | 'simulado';

interface FlatCategory {
    _id: string;
    name: string;
    level: number;
    parentId: string | null;
    questionsCount: number;
}

interface SelectedCategory {
    id: string;
    breadcrumb: string;
}

interface SavedFormState {
    statement: string;
    statementImage: string;
    alternatives: any[];
    difficulty: string;
    explanationText: string;
    explanationVideoUrl: string;
    status: QuestionStatus;
    selectedCategories: SelectedCategory[];
    sel1: string; sel2: string; sel3: string; sel4: string;
}

interface ImportQueueItem {
    raw: any;
    status: QuestionStatus;
    savedState: SavedFormState | null;
}

interface QuestionFormTabProps {
    onShowToast: (message: string, type: 'success' | 'error' | 'info') => void;
    editingQuestionId: string | null;
    onSaved: () => void;
    onCancel: () => void;
    draft?: any;
    onDraftChange?: (draft: any) => void;
}

interface AltRowProps {
    alt: { letter: string; text: string; isCorrect: boolean };
    index: number;
    total: number;
    onChangeText: (index: number, value: string) => void;
    onSelectCorrect: (index: number) => void;
    onRemove: (index: number) => void;
}

const AltRow: React.FC<AltRowProps> = ({
    alt, index, total, onChangeText, onSelectCorrect, onRemove
}) => {
    const textareaRef = useRef<HTMLTextAreaElement>(null);

    useEffect(() => {
        const el = textareaRef.current;
        if (!el) return;
        el.style.height = 'auto';
        el.style.height = `${el.scrollHeight}px`;
    }, [alt.text]);

    return (
        <div className={`qft__alt-row ${alt.isCorrect ? 'qft__alt-row--ok' : ''}`}>
            <input
                type="radio"
                name="qft-correct"
                className="qft__radio"
                checked={alt.isCorrect}
                onChange={() => onSelectCorrect(index)}
            />
            <span className="qft__alt-letter">{alt.letter}</span>
            <textarea
                ref={textareaRef}
                className="qft__alt-textarea"
                value={alt.text}
                onChange={e => onChangeText(index, e.target.value)}
                placeholder={`Alternativa ${alt.letter}`}
                rows={1}
            />
            {alt.isCorrect && <CheckCircle size={14} className="qft__ok-icon" />}
            {total > 2 && (
                <button className="qft__alt-del" onClick={() => onRemove(index)}>
                    <Trash2 size={13} />
                </button>
            )}
        </div>
    );
};

const LETTERS = ['A', 'B', 'C', 'D', 'E'];

/* ═══════════════════════════════════════════════════════════════
   FUNÇÕES PURAS NO ESCOPO DO MÓDULO
═══════════════════════════════════════════════════════════════ */

function flattenTree(
    nodes: CategoryNode[],
    parentId: string | null = null
): FlatCategory[] {
    const result: FlatCategory[] = [];
    for (const node of nodes) {
        result.push({
            _id: node._id,
            name: node.name,
            level: node.level,
            parentId,
            questionsCount: node.questionsCount,
        });
        if (node.children?.length)
            result.push(...flattenTree(node.children, node._id));
    }
    return result;
}

function getChain(id: string, cats: FlatCategory[]): string[] {
    const chain: string[] = [];
    let current = cats.find(c => c._id === id);
    while (current) {
        chain.unshift(current._id);
        const parentId = current.parentId;
        current = parentId ? cats.find(c => c._id === parentId) : undefined;
    }
    return chain;
}

function buildLegacyCategory(
    id: string,
    cats: FlatCategory[]
): { level1: string | null; level2: string | null; level3: string | null; level4: string | null } {
    const getName = (i: string) => cats.find(c => c._id === i)?.name ?? null;
    const chain = getChain(id, cats);
    return {
        level1: chain[0] ? getName(chain[0]) : null,
        level2: chain[1] ? getName(chain[1]) : null,
        level3: chain[2] ? getName(chain[2]) : null,
        level4: chain[3] ? getName(chain[3]) : null,
    };
}

function resolveIdsFromLegacy(
    legacy: { level1?: string | null; level2?: string | null; level3?: string | null; level4?: string | null },
    cats: FlatCategory[]
): string[] {
    const l1 = cats.find(c => c.level === 1 && c.name === legacy.level1);
    if (!l1) return [];
    if (!legacy.level2) return [l1._id];
    const l2 = cats.find(c => c.level === 2 && c.name === legacy.level2 && c.parentId === l1._id);
    if (!l2) return [l1._id];
    if (!legacy.level3) return [l2._id];
    const l3 = cats.find(c => c.level === 3 && c.name === legacy.level3 && c.parentId === l2._id);
    if (!l3) return [l2._id];
    if (!legacy.level4) return [l3._id];
    const l4 = cats.find(c => c.level === 4 && c.name === legacy.level4 && c.parentId === l3._id);
    return l4 ? [l4._id] : [l3._id];
}

async function uploadToCloudinary(file: File, folder: string): Promise<string> {
    const formData = new FormData();
    formData.append('image', file);
    formData.append('subdeckName', folder);
    const token = localStorage.getItem('accessToken');
    const res = await fetch('/api/upload/card-image-cloudinary', {
        method: 'POST',
        body: formData,
        headers: { Authorization: `Bearer ${token}` },
    });
    const data = await res.json();
    if (!res.ok || !data.url) throw new Error(data.message || 'Erro no upload');
    return data.url;
}

function getYoutubeEmbedUrl(url: string): string | null {
    if (!url) return null;
    const m = url.match(
        /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([a-zA-Z0-9_-]{11})/
    );
    return m ? `https://www.youtube.com/embed/${m[1]}` : null;
}

function stripHtml(html: string): string {
    return html.replace(/<[^>]*>/g, '').trim();
}

function stripHtmlFormatted(html: string): string {
    return html
        .replace(/<\/?(p|div|h[1-6]|blockquote|pre|section|article)[^>]*>/gi, '\n')
        .replace(/<li[^>]*>/gi, '\n• ')
        .replace(/<\/li>/gi, '')
        .replace(/<br\s*\/?>/gi, '\n')
        .replace(/<[^>]*>/g, '')
        .replace(/&nbsp;/g, ' ')
        .replace(/&amp;/g, '&')
        .replace(/&lt;/g, '<')
        .replace(/&gt;/g, '>')
        .replace(/&quot;/g, '"')
        .replace(/\n{3,}/g, '\n\n')
        .trim();
}

/* ═══════════════════════════════════════════════════════════════
   COMPONENTE PRINCIPAL
═══════════════════════════════════════════════════════════════ */
const QuestionFormTab: React.FC<QuestionFormTabProps> = ({
    onShowToast,
    editingQuestionId,
    onSaved,
    onCancel,
    draft,
    onDraftChange,
}) => {
    /* ── Form states ─────────────────────────────────────── */
    const [allCategories, setAllCategories] = useState<FlatCategory[]>([]);
    const [statement, setStatement] = useState<string>(draft?.statement ?? '');
    const [statementImage, setStatementImage] = useState<string>(draft?.statementImage ?? '');
    const [statementImageUploading, setStatementImageUploading] = useState(false);
    const [alternatives, setAlternatives] = useState<any[]>(
        draft?.alternatives ?? [
            { letter: 'A', text: '', isCorrect: false },
            { letter: 'B', text: '', isCorrect: false },
        ]
    );
    const [difficulty, setDifficulty] = useState<'facil' | 'medio' | 'dificil' | ''>(draft?.difficulty ?? '');
    const [explanationText, setExplanationText] = useState<string>(draft?.explanationText ?? '');
    const [explanationVideoUrl, setExplanationVideoUrl] = useState<string>(draft?.explanationVideoUrl ?? '');
    const [status, setStatus] = useState<QuestionStatus>(draft?.status ?? 'rascunho');

    const [selectedCategories, setSelectedCategories] = useState<SelectedCategory[]>(draft?.selectedCategories ?? []);
    const [sel1, setSel1] = useState<string>(draft?.sel1 ?? '');
    const [sel2, setSel2] = useState<string>(draft?.sel2 ?? '');
    const [sel3, setSel3] = useState<string>(draft?.sel3 ?? '');
    const [sel4, setSel4] = useState<string>(draft?.sel4 ?? '');

    const [pendingCatIds, setPendingCatIds] = useState<string[] | null>(null);
    const [pendingLegacyCat, setPendingLegacyCat] = useState<any | null>(null);
    // Incrementado toda vez que os editores ricos devem recarregar do zero
    const [editorKey, setEditorKey] = useState(0);

    const [zoomUrl, setZoomUrl] = useState<string | null>(null);
    const [zoomScale, setZoomScale] = useState(1);
    const [showPasteModal, setShowPasteModal] = useState(false);
    const [showCreateCategory, setShowCreateCategory] = useState(false);
    const [newCatName, setNewCatName] = useState('');
    const [newCatLevel, setNewCatLevel] = useState(1);
    const [newCatParentId, setNewCatParentId] = useState('');
    const [creatingCat, setCreatingCat] = useState(false);

    /* ── Navigator states ─────────────────────────────────── */
    const [editorMode, setEditorMode] = useState<EditorMode>('create');
    const [activeEditId, setActiveEditId] = useState<string | null>(null);

    // Session tracking — questões criadas/importadas nesta sessão
    const [sessionQuestions, setSessionQuestions] = useState<any[]>([]);

    const addQuestionToSession = (q: any) => {
        if (!q?._id) return;
        setSessionQuestions(prev => prev.some(x => x._id === q._id) ? prev : [q, ...prev]);
    };

    // Edit queue
    const [editQueue, setEditQueue] = useState<any[]>([]);
    const [editQueueIndex, setEditQueueIndex] = useState(0);
    const [editQueueLoading, setEditQueueLoading] = useState(false);
    const [navStatus, setNavStatus] = useState('');
    const [navDifficulty, setNavDifficulty] = useState('');
    const [navPreset, setNavPreset] = useState('');
    const [navCat1, setNavCat1] = useState('');
    const [navCat2, setNavCat2] = useState('');

    // Import queue
    const [importQueue, setImportQueue] = useState<ImportQueueItem[]>([]);
    const [importIndex, setImportIndex] = useState(0);
    const [importImporting, setImportImporting] = useState(false);
    const [, setJsonInput] = useState('');
    const [jsonParseError, setJsonParseError] = useState<string | null>(null);
    const navFileInputRef = useRef<HTMLInputElement>(null);

    // Exam metadata (prova/simulado modes)
    const [examName, setExamName] = useState('');
    const [examInstitution, setExamInstitution] = useState('');
    const [examYear, setExamYear] = useState<number | ''>('');
    const [examArea, setExamArea] = useState('');
    const [examLevel, setExamLevel] = useState('');
    const [examDuration, setExamDuration] = useState(180);
    const [examImporting, setExamImporting] = useState(false);

    // Revisor mode
    const [revisorQueue, setRevisorQueue] = useState<any[]>([]);
    const [revisorQueueIndex, setRevisorQueueIndex] = useState(0);
    const [revisorQueueLoading, setRevisorQueueLoading] = useState(false);
    const [revisorFilterRevisor, setRevisorFilterRevisor] = useState('');
    const [revisorFilterCat1, setRevisorFilterCat1] = useState('');
    const [revisorFilterCat2, setRevisorFilterCat2] = useState('');
    const [revisorFilterAnalysis, setRevisorFilterAnalysis] = useState<'com' | 'sem' | ''>('com');
    const [revisoresList, setRevisoresList] = useState<any[]>([]);
    const [questionAnalyses, setQuestionAnalyses] = useState<any[]>([]);
    const [analysesLoading, setAnalysesLoading] = useState(false);
    const [collapsedAnalyses, setCollapsedAnalyses] = useState<Set<number>>(new Set());

    /* ── Derived ──────────────────────────────────────────── */
    const level1Opts = allCategories.filter(c => c.level === 1);
    const level2Opts = allCategories.filter(c => c.level === 2 && (!sel1 || c.parentId === sel1));
    const level3Opts = allCategories.filter(c => c.level === 3 && (!sel2 || c.parentId === sel2));
    const level4Opts = allCategories.filter(c => c.level === 4 && (!sel3 || c.parentId === sel3));
    const tempFinalId = sel4 || sel3 || sel2 || sel1 || '';
    const isExamMode = editorMode === 'prova' || editorMode === 'simulado';
    const hasEditQueue = editQueue.length > 0;
    const hasImportQueue = importQueue.length > 0;
    const hasRevisorQueue = revisorQueue.length > 0;
    const revisorL1Cat = allCategories.find(c => c.level === 1 && c.name === revisorFilterCat1);
    const revisorL2Opts = revisorL1Cat
        ? allCategories.filter(c => c.level === 2 && c.parentId === revisorL1Cat._id)
        : [];
    const navL1Cat = allCategories.find(c => c.level === 1 && c.name === navCat1);
    const navL2Opts = navL1Cat
        ? allCategories.filter(c => c.level === 2 && c.parentId === navL1Cat._id)
        : [];

    /* ── Effects ──────────────────────────────────────────── */
    useEffect(() => { loadCategories(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (editingQuestionId) {
            setActiveEditId(editingQuestionId);
            setEditorMode('edit');
            loadQuestion(editingQuestionId);
        } else if (!draft && editorMode === 'create') {
            resetForm();
        }
    }, [editingQuestionId]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (allCategories.length === 0) return;
        if (pendingCatIds && pendingCatIds.length > 0) {
            rebuildCategories(pendingCatIds, allCategories);
            setPendingCatIds(null);
            return;
        }
        if (pendingLegacyCat?.level1) {
            const ids = resolveIdsFromLegacy(pendingLegacyCat, allCategories);
            if (ids.length > 0) rebuildCategories(ids, allCategories);
            setPendingLegacyCat(null);
        }
    }, [allCategories, pendingCatIds, pendingLegacyCat]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (editorMode !== 'create' || activeEditId) return;
        onDraftChange?.({
            statement, statementImage, alternatives, difficulty,
            explanationText, explanationVideoUrl, status,
            selectedCategories, sel1, sel2, sel3, sel4,
        });
    }, [ // eslint-disable-line react-hooks/exhaustive-deps
        editorMode, activeEditId, statement, statementImage, alternatives, difficulty,
        explanationText, explanationVideoUrl, status,
        selectedCategories, sel1, sel2, sel3, sel4,
    ]);

    /* ── Category helpers ─────────────────────────────────── */
    const rebuildCategories = (catIds: string[], cats: FlatCategory[]) => {
        const getName = (id: string) => cats.find(c => c._id === id)?.name ?? '';
        const chips: SelectedCategory[] = catIds
            .filter(id => cats.some(c => c._id === id))
            .map(id => ({ id, breadcrumb: getChain(id, cats).map(getName).join(' › ') }));
        setSelectedCategories(chips);
        if (catIds.length > 0) {
            const chain = getChain(catIds[0], cats);
            setSel1(chain[0] ?? '');
            setSel2(chain[1] ?? '');
            setSel3(chain[2] ?? '');
            setSel4(chain[3] ?? '');
        }
    };

    const getBreadcrumb = (s1: string, s2: string, s3: string, s4: string) => {
        const getName = (id: string) => allCategories.find(c => c._id === id)?.name ?? '';
        return [s1, s2, s3, s4].filter(Boolean).map(getName).join(' › ');
    };

    const handleAddCategory = () => {
        if (!tempFinalId) { onShowToast('Selecione ao menos o campo de estudo', 'error'); return; }
        if (selectedCategories.find(c => c.id === tempFinalId)) { onShowToast('Categoria já adicionada', 'info'); return; }
        setSelectedCategories(prev => [...prev, { id: tempFinalId, breadcrumb: getBreadcrumb(sel1, sel2, sel3, sel4) }]);
        setSel1(''); setSel2(''); setSel3(''); setSel4('');
    };

    const handleInlineCreateCategory = async () => {
        if (!newCatName.trim()) { onShowToast('Nome é obrigatório', 'error'); return; }
        if (newCatLevel > 1 && !newCatParentId) { onShowToast('Selecione o nível pai', 'error'); return; }
        setCreatingCat(true);
        try {
            await questionService.createCategory({
                name: newCatName.trim(),
                level: newCatLevel,
                parentId: newCatParentId || null,
            });
            const tree = await questionService.getCategoriesTree();
            const fresh = flattenTree(tree);
            setAllCategories(fresh);
            const created = fresh.find(
                c => c.name === newCatName.trim() && c.level === newCatLevel
                    && (newCatLevel === 1 || c.parentId === newCatParentId)
            );
            if (created) rebuildCategories([created._id], fresh);
            onShowToast('Categoria criada e selecionada!', 'success');
            setShowCreateCategory(false);
            setNewCatName(''); setNewCatLevel(1); setNewCatParentId('');
        } catch {
            onShowToast('Erro ao criar categoria', 'error');
        } finally {
            setCreatingCat(false);
        }
    };

    /* ── Data loading ─────────────────────────────────────── */
    const loadCategories = async () => {
        try {
            const tree = await questionService.getCategoriesTree();
            setAllCategories(flattenTree(tree));
        } catch { /* silencioso */ }
    };

    const loadQuestion = async (id: string) => {
        try {
            const data = await questionService.getQuestionAdmin(id);
            applyQuestionData(data.question);
        } catch {
            onShowToast('Erro ao carregar questão', 'error');
        }
    };

    const applyQuestionData = (q: any) => {
        setEditorKey(k => k + 1);
        setStatement(q.statement || '');
        setStatementImage(q.statementImage || '');
        setAlternatives(q.alternatives?.length ? q.alternatives : [
            { letter: 'A', text: '', isCorrect: false },
            { letter: 'B', text: '', isCorrect: false },
        ]);
        setExplanationText(q.explanationText || '');
        setExplanationVideoUrl(q.explanationVideoUrl || '');
        setDifficulty(q.difficulty || '');
        setStatus(q.status || 'rascunho');

        const catIds: string[] =
            q.categoryIds?.length ? q.categoryIds
                : q.categoryId ? [q.categoryId]
                    : [];

        if (allCategories.length > 0) {
            if (catIds.length > 0) {
                rebuildCategories(catIds, allCategories);
            } else if (q.category?.level1) {
                const ids = resolveIdsFromLegacy(q.category, allCategories);
                if (ids.length > 0) rebuildCategories(ids, allCategories);
                else { setSelectedCategories([]); setSel1(''); setSel2(''); setSel3(''); setSel4(''); }
            } else {
                setSelectedCategories([]); setSel1(''); setSel2(''); setSel3(''); setSel4('');
            }
        } else {
            if (catIds.length > 0) setPendingCatIds(catIds);
            else if (q.category?.level1) setPendingLegacyCat(q.category);
        }
    };

    const resetForm = () => {
        setStatement(''); setStatementImage('');
        setAlternatives([
            { letter: 'A', text: '', isCorrect: false },
            { letter: 'B', text: '', isCorrect: false },
        ]);
        setSel1(''); setSel2(''); setSel3(''); setSel4('');
        setSelectedCategories([]); setDifficulty('');
        setExplanationText(''); setExplanationVideoUrl(''); setStatus('rascunho');
    };

    /* ── Form state capture / restore ─────────────────────── */
    const captureFormState = (): SavedFormState => ({
        statement, statementImage, alternatives, difficulty,
        explanationText, explanationVideoUrl, status,
        selectedCategories, sel1, sel2, sel3, sel4,
    });

    const restoreFormState = (s: SavedFormState) => {
        setEditorKey(k => k + 1);
        setStatement(s.statement);
        setStatementImage(s.statementImage);
        setAlternatives(s.alternatives);
        setDifficulty(s.difficulty as any);
        setExplanationText(s.explanationText);
        setExplanationVideoUrl(s.explanationVideoUrl);
        setStatus(s.status);
        setSelectedCategories(s.selectedCategories);
        setSel1(s.sel1); setSel2(s.sel2); setSel3(s.sel3); setSel4(s.sel4);
    };

    /* ── Copy to clipboard ────────────────────────────────── */
    const copyQuestion = () => {
        const stmtText = stripHtml(statement);
        const altsText = alternatives.map(a => `${a.letter}) ${a.text}`).join('\n');
        const correct = alternatives.find(a => a.isCorrect);
        const text = [
            '🧠 ENUNCIADO', stmtText, '',
            '🅰️ ALTERNATIVAS', altsText, '',
            '✅ CORRETA',
            correct ? `${correct.letter}) ${correct.text}` : '—',
        ].join('\n');
        navigator.clipboard.writeText(text).then(
            () => onShowToast('Questão copiada!', 'success'),
            () => onShowToast('Erro ao copiar', 'error')
        );
    };

    const copyQuestionWithResolution = () => {
        const stmtText = stripHtml(statement);
        const altsText = alternatives.map(a => `${a.letter}) ${a.text}`).join('\n');
        const correct = alternatives.find(a => a.isCorrect);
        const resolutionText = stripHtmlFormatted(explanationText);
        const parts: string[] = [
            '🧠 ENUNCIADO', stmtText, '',
            '🅰️ ALTERNATIVAS', altsText, '',
            '✅ CORRETA',
            correct ? `${correct.letter}) ${correct.text}` : '—',
        ];
        if (resolutionText) parts.push('', '📖 RESOLUÇÃO', resolutionText);
        navigator.clipboard.writeText(parts.join('\n')).then(
            () => onShowToast('Copiado com resolução!', 'success'),
            () => onShowToast('Erro ao copiar', 'error')
        );
    };

    /* ── EDIT QUEUE ──────────────────────────────────────── */
    const loadEditQueue = async () => {
        // Preset "Desta sessão" usa o array local, sem chamada à API
        if (navPreset === 'desta-sessao') {
            if (sessionQuestions.length === 0) {
                onShowToast('Nenhuma questão criada ou importada nesta sessão ainda', 'info');
                return;
            }
            setEditQueue(sessionQuestions);
            setEditQueueIndex(0);
            setActiveEditId(sessionQuestions[0]._id);
            applyQuestionData(sessionQuestions[0]);
            return;
        }

        setEditQueueLoading(true);
        setEditQueue([]);
        try {
            const filters: any = { limit: 500, page: 1 };
            if (navStatus) filters.status = navStatus;
            if (navDifficulty) filters.difficulty = navDifficulty;
            if (navCat1) filters.level1 = navCat1;
            if (navCat2) filters.level2 = navCat2;
            if (navPreset === 'publicadas-hoje' || navPreset === 'publicadas-ontem') {
                filters.status = 'publicado';
            }

            const data = await questionService.getQuestionsAdmin(filters);
            let questions: any[] = data.questions || data || [];

            if (navPreset === 'sem-resolucao') {
                questions = questions.filter((q: any) => !stripHtml(q.explanationText || ''));
            } else if (navPreset === 'sem-categoria') {
                questions = questions.filter((q: any) =>
                    !q.categoryIds?.length && !q.categoryId && !q.category?.level1
                );
            } else if (navPreset === 'hoje') {
                const todayStart = new Date();
                todayStart.setHours(0, 0, 0, 0);
                questions = questions.filter((q: any) =>
                    q.createdAt && new Date(q.createdAt) >= todayStart
                );
            } else if (navPreset === 'publicadas-hoje') {
                const todayStart = new Date();
                todayStart.setHours(0, 0, 0, 0);
                questions = questions.filter((q: any) =>
                    q.status === 'publicado' && q.updatedAt && new Date(q.updatedAt) >= todayStart
                );
            } else if (navPreset === 'publicadas-ontem') {
                const todayStart = new Date();
                todayStart.setHours(0, 0, 0, 0);
                const yesterdayStart = new Date(todayStart);
                yesterdayStart.setDate(yesterdayStart.getDate() - 1);
                questions = questions.filter((q: any) =>
                    q.status === 'publicado' && q.updatedAt &&
                    new Date(q.updatedAt) >= yesterdayStart && new Date(q.updatedAt) < todayStart
                );
            }

            if (questions.length === 0) {
                onShowToast('Nenhuma questão encontrada com esses filtros', 'info');
                return;
            }
            setEditQueue(questions);
            setEditQueueIndex(0);
            setActiveEditId(questions[0]._id);
            applyQuestionData(questions[0]);
        } catch {
            onShowToast('Erro ao carregar questões', 'error');
        } finally {
            setEditQueueLoading(false);
        }
    };

    const navigateEditQueue = (newIndex: number) => {
        if (newIndex < 0 || newIndex >= editQueue.length) return;
        setEditQueueIndex(newIndex);
        const q = editQueue[newIndex];
        setActiveEditId(q._id);
        applyQuestionData(q);
    };

    /* ── IMPORT QUEUE ────────────────────────────────────── */
    const processImportJson = (text: string) => {
        setJsonParseError(null);
        try {
            const obj = JSON.parse(text.trim());
            const questions = Array.isArray(obj) ? obj : obj.questions;
            if (!Array.isArray(questions)) throw new Error('JSON deve ter um array "questions" ou ser um array direto.');
            if (questions.length === 0) throw new Error('Array de questões está vazio.');
            if (questions.length > 200) throw new Error('Máximo de 200 questões por importação.');
            const defaultStatus: QuestionStatus = editorMode === 'simulado' ? 'simulado' : editorMode === 'prova' ? 'prova' : 'rascunho';
            const queue: ImportQueueItem[] = questions.map(raw => ({
                raw,
                status: (raw.status as QuestionStatus) || defaultStatus,
                savedState: null,
            }));
            setImportQueue(queue);
            setImportIndex(0);
            setJsonInput('');
            loadImportQueueItem(queue[0]);
            onShowToast(`${queue.length} questão(ões) prontas para revisar`, 'success');
        } catch (err: any) {
            setJsonParseError(err.message || 'JSON inválido');
        }
    };

    const loadImportQueueItem = (item: ImportQueueItem) => {
        if (item.savedState) {
            restoreFormState(item.savedState);
        } else {
            applyQuestionData(item.raw);
            setStatus(item.status);
        }
        setActiveEditId(null);
    };

    const navigateImportQueue = (newIndex: number) => {
        if (newIndex < 0 || newIndex >= importQueue.length) return;
        const saved = captureFormState();
        // save state for current item before navigating
        setImportQueue(prev => {
            const next = [...prev];
            next[importIndex] = { ...next[importIndex], savedState: saved, status: saved.status };
            return next;
        });
        setImportIndex(newIndex);
        // load next item from current (pre-update) queue state — newIndex is unmodified
        loadImportQueueItem(importQueue[newIndex]);
    };

    const importSingle = async () => {
        if (!importQueue.length) return;
        setImportImporting(true);
        try {
            const primaryCatId = selectedCategories[0]?.id;
            const payload = {
                statement, statementImage: statementImage || null,
                alternatives,
                explanationText: explanationText || null,
                explanationVideoUrl: explanationVideoUrl || null,
                category: primaryCatId ? buildLegacyCategory(primaryCatId, allCategories) : null,
                categoryId: primaryCatId || null,
                categoryIds: selectedCategories.map(c => c.id),
                difficulty: difficulty || null,
                tags: [],
                status,
            };
            const response = await questionService.importQuestions([payload]);
            if (response.results.imported > 0) {
                onShowToast('Questão importada!', 'success');
                // Busca a questão mais recente para registrar na sessão
                try {
                    const recent = await questionService.getQuestionsAdmin({ limit: 1, page: 1 });
                    if (recent.questions?.[0]) addQuestionToSession(recent.questions[0]);
                } catch { /* silencioso */ }
                discardImportQuestion(importIndex, true);
            } else {
                onShowToast(response.results.errors[0]?.error || 'Erro ao importar', 'error');
            }
        } catch (err: any) {
            onShowToast(err.message || 'Erro ao importar', 'error');
        } finally {
            setImportImporting(false);
        }
    };

    const importAll = async () => {
        if (!importQueue.length) return;
        setImportImporting(true);
        try {
            const saved = captureFormState();
            const updatedQueue = importQueue.map((item, i) =>
                i === importIndex ? { ...item, savedState: saved, status: saved.status } : item
            );
            const questions = updatedQueue.map(item => {
                const s = item.savedState;
                if (s) {
                    const pid = s.selectedCategories[0]?.id;
                    return {
                        statement: s.statement,
                        statementImage: s.statementImage || null,
                        alternatives: s.alternatives,
                        explanationText: s.explanationText || null,
                        explanationVideoUrl: s.explanationVideoUrl || null,
                        category: pid ? buildLegacyCategory(pid, allCategories) : null,
                        categoryId: pid || null,
                        categoryIds: s.selectedCategories.map(c => c.id),
                        difficulty: s.difficulty || null,
                        tags: [],
                        status: s.status,
                    };
                }
                return { ...item.raw, status: item.status };
            });
            const response = await questionService.importQuestions(questions);
            if (response.results.failed === 0) {
                const importedCount = response.results.imported;
                onShowToast(`${importedCount} questão(ões) importada(s)!`, 'success');
                // Busca as questões mais recentes para registrar na sessão
                try {
                    const recent = await questionService.getQuestionsAdmin({ limit: importedCount, page: 1 });
                    recent.questions?.forEach((q: any) => addQuestionToSession(q));
                } catch { /* silencioso */ }
                setImportQueue([]); setImportIndex(0); resetForm();
            } else if (response.results.imported > 0) {
                onShowToast(`Parcial: ${response.results.imported} ok, ${response.results.failed} falhas`, 'info');
            } else {
                onShowToast('Nenhuma questão importada', 'error');
            }
        } catch (err: any) {
            onShowToast(err.message || 'Erro ao importar', 'error');
        } finally {
            setImportImporting(false);
        }
    };

    const discardImportQuestion = (idx: number, silent?: boolean) => {
        const next = importQueue.filter((_, i) => i !== idx);
        if (next.length === 0) {
            setImportQueue([]); setImportIndex(0); resetForm();
            if (!silent) onShowToast('Fila concluída', 'info');
            return;
        }
        const newIdx = Math.min(idx, next.length - 1);
        setImportQueue(next);
        setImportIndex(newIdx);
        loadImportQueueItem(next[newIdx]);
        if (!silent) onShowToast('Questão descartada', 'info');
    };

    const importExamFinal = async () => {
        if (!examName.trim()) { onShowToast('Nome é obrigatório', 'error'); return; }
        if (editorMode === 'prova' && !examInstitution.trim()) {
            onShowToast('Instituição é obrigatória para provas', 'error'); return;
        }
        if (!importQueue.length) { onShowToast('Nenhuma questão na fila', 'error'); return; }

        setExamImporting(true);
        try {
            const saved = captureFormState();
            const updatedQueue = importQueue.map((item, i) =>
                i === importIndex ? { ...item, savedState: saved, status: saved.status } : item
            );
            const questions = updatedQueue.map(item => {
                const s = item.savedState;
                if (s) {
                    const pid = s.selectedCategories[0]?.id;
                    return {
                        statement: s.statement,
                        statementImage: s.statementImage || null,
                        alternatives: s.alternatives,
                        explanationText: s.explanationText || null,
                        explanationVideoUrl: s.explanationVideoUrl || null,
                        category: pid ? buildLegacyCategory(pid, allCategories) : undefined,
                        categoryId: pid || null,
                        categoryIds: s.selectedCategories.map(c => c.id),
                        difficulty: s.difficulty || null,
                        tags: [],
                        status: s.status,
                    };
                }
                return { ...item.raw, status: item.status };
            });

            const { examService } = await import('../../../services/examService');
            const result = await (examService as any).importExam({
                name: examName,
                institution: examInstitution || '',
                year: examYear !== '' ? Number(examYear) : undefined,
                area: examArea || undefined,
                level: examLevel || undefined,
                officialDuration: examDuration,
                questions,
                questionStatus: 'prova',
                type: editorMode,
            });

            const r = result.results;
            const label = editorMode === 'prova' ? 'Prova' : 'Simulado';
            onShowToast(`${label} importado! ${r.imported} questão(ões)${r.failed > 0 ? `, ${r.failed} falha(s)` : ''}.`, 'success');
            setImportQueue([]); setImportIndex(0); resetForm();
            setExamName(''); setExamInstitution(''); setExamYear('');
            setExamArea(''); setExamLevel(''); setExamDuration(180);
        } catch (err: any) {
            onShowToast(err.message || 'Erro ao importar', 'error');
        } finally {
            setExamImporting(false);
        }
    };

    const setImportItemStatus = (newStatus: QuestionStatus) => {
        setStatus(newStatus);
        setImportQueue(prev => {
            const next = [...prev];
            next[importIndex] = { ...next[importIndex], status: newStatus };
            return next;
        });
    };

    const setAllImportStatus = (newStatus: QuestionStatus) => {
        setStatus(newStatus);
        setImportQueue(prev => prev.map(item => ({
            ...item, status: newStatus,
            savedState: item.savedState ? { ...item.savedState, status: newStatus } : null,
        })));
        onShowToast(`Todas definidas como ${newStatus}`, 'info');
    };

    /* ── REVISOR QUEUE ───────────────────────────────────── */
    const loadRevisoresList = async () => {
        try {
            const token = localStorage.getItem('accessToken');
            const res = await fetch('/api/admin/features/revisores/list', {
                headers: { Authorization: `Bearer ${token}` }
            });
            const data = await res.json();
            setRevisoresList(data.revisores || []);
        } catch { /* silencioso */ }
    };

    const loadRevisorQueue = async () => {
        setRevisorQueueLoading(true);
        setRevisorQueue([]);
        setQuestionAnalyses([]);
        try {
            const data = await questionService.getAnalysisQuestions(1, 500);
            let questions: any[] = data.questions || [];
            if (revisorFilterAnalysis === 'com') {
                questions = questions.filter((q: any) => (q.analysisCount || 0) > 0);
            } else if (revisorFilterAnalysis === 'sem') {
                questions = questions.filter((q: any) => (q.analysisCount || 0) === 0);
            }
            if (revisorFilterCat1) {
                questions = questions.filter((q: any) => q.category?.level1 === revisorFilterCat1);
            }
            if (revisorFilterCat2) {
                questions = questions.filter((q: any) => q.category?.level2 === revisorFilterCat2);
            }
            if (revisorFilterRevisor) {
                questions = questions.filter((q: any) =>
                    Array.isArray(q.reviserIds) && q.reviserIds.includes(revisorFilterRevisor)
                );
            }
            if (questions.length === 0) {
                onShowToast('Nenhuma questão encontrada com esses filtros', 'info');
                return;
            }
            setRevisorQueue(questions);
            setRevisorQueueIndex(0);
            const first = questions[0];
            setActiveEditId(first._id);
            applyQuestionData(first);
            loadQuestionAnalysesFor(first._id);
        } catch {
            onShowToast('Erro ao carregar questões', 'error');
        } finally {
            setRevisorQueueLoading(false);
        }
    };

    const navigateRevisorQueue = (newIndex: number) => {
        if (newIndex < 0 || newIndex >= revisorQueue.length) return;
        setRevisorQueueIndex(newIndex);
        const q = revisorQueue[newIndex];
        setActiveEditId(q._id);
        applyQuestionData(q);
        loadQuestionAnalysesFor(q._id);
    };

    const loadQuestionAnalysesFor = async (id: string) => {
        setAnalysesLoading(true);
        setQuestionAnalyses([]);
        setCollapsedAnalyses(new Set());
        try {
            const analyses = await questionService.getQuestionAnalyses(id);
            setQuestionAnalyses(analyses || []);
        } catch { /* silencioso */ } finally {
            setAnalysesLoading(false);
        }
    };

    const toggleAnalysis = (i: number) => {
        setCollapsedAnalyses(prev => {
            const next = new Set(prev);
            if (next.has(i)) next.delete(i); else next.add(i);
            return next;
        });
    };

    const acceptSuggestedStatement = (text: string) => {
        setEditorKey(k => k + 1);
        setStatement(text);
        onShowToast('Enunciado atualizado', 'info');
    };

    const acceptSuggestedAlt = (letter: string, text: string) => {
        setAlternatives(prev => prev.map((a: any) => a.letter === letter ? { ...a, text } : a));
        onShowToast(`Alternativa ${letter} atualizada`, 'info');
    };

    /* ── Mode switch ─────────────────────────────────────── */
    const switchMode = (mode: EditorMode) => {
        if (mode === editorMode) return;
        setEditorMode(mode);
        setActiveEditId(null);
        resetForm();
        setQuestionAnalyses([]);
        if (mode === 'create' || mode === 'edit') {
            setEditQueue([]); setEditQueueIndex(0);
        }
        if (mode === 'import' || mode === 'prova' || mode === 'simulado') {
            setImportQueue([]); setImportIndex(0);
            setJsonInput(''); setJsonParseError(null);
        }
        if (mode === 'prova' || mode === 'simulado') {
            setExamName(''); setExamInstitution(''); setExamYear('');
            setExamArea(''); setExamLevel(''); setExamDuration(180);
            setStatus(mode === 'simulado' ? 'simulado' : 'prova');
        }
        if (mode === 'revisor') {
            setRevisorQueue([]); setRevisorQueueIndex(0);
            loadRevisoresList();
        }
    };

    /* ── Form handlers ───────────────────────────────────── */
    const handleStatementImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setStatementImageUploading(true);
        try {
            const url = await uploadToCloudinary(file, 'questoes-enunciado');
            setStatement(prev => prev + `<img src="${url}" alt="imagem" style="max-width:100%;border-radius:8px;margin:8px 0;" />`);
            onShowToast('Imagem adicionada ao enunciado', 'success');
        } catch { onShowToast('Erro no upload da imagem', 'error'); }
        finally { setStatementImageUploading(false); e.target.value = ''; }
    };

    const handleAlternativeChange = (index: number, field: 'text' | 'isCorrect', value: any) => {
        const newAlts = [...alternatives];
        if (field === 'isCorrect' && value === true) newAlts.forEach((a, i) => { a.isCorrect = i === index; });
        else newAlts[index] = { ...newAlts[index], [field]: value };
        setAlternatives(newAlts);
    };

    const handleAddAlternative = () => {
        if (alternatives.length >= 5) return;
        setAlternatives([...alternatives, { letter: LETTERS[alternatives.length], text: '', isCorrect: false }]);
    };

    const handleRemoveAlternative = (index: number) => {
        if (alternatives.length <= 2) { onShowToast('Mínimo de 2 alternativas', 'error'); return; }
        setAlternatives(
            alternatives.filter((_, i) => i !== index).map((a, i) => ({ ...a, letter: LETTERS[i] }))
        );
    };

    const handlePasteAlternatives = (lines: string[]) => {
        setAlternatives(lines.map((text, i) => ({ letter: LETTERS[i], text, isCorrect: false })));
        setShowPasteModal(false);
        onShowToast(`${lines.length} alternativas preenchidas`, 'success');
    };

    const handleSave = async () => {
        const statementText = statement.replace(/<[^>]*>/g, '').trim();
        if (!statementText) { onShowToast('Enunciado é obrigatório', 'error'); return; }
        if (alternatives.filter(a => a.isCorrect).length !== 1) {
            onShowToast('Selecione exatamente 1 alternativa correta', 'error'); return;
        }
        if (selectedCategories.length === 0) {
            onShowToast('Selecione ao menos uma categoria', 'error'); return;
        }
        const primaryCatId = selectedCategories[0].id;
        try {
            const payload = {
                statement, statementImage: statementImage || null,
                alternatives,
                explanationText: explanationText || null,
                explanationVideoUrl: explanationVideoUrl || null,
                category: buildLegacyCategory(primaryCatId, allCategories),
                categoryId: primaryCatId,
                categoryIds: selectedCategories.map(c => c.id),
                difficulty: difficulty || null,
                tags: [],
                status,
            };
            if (activeEditId) {
                await questionService.updateQuestion(activeEditId, payload);
                onShowToast('Questão atualizada', 'success');
                if (editorMode === 'edit' && hasEditQueue) {
                    // update cached data in the queue so navigation reflects the change
                    setEditQueue(prev => {
                        const next = [...prev];
                        next[editQueueIndex] = { ...next[editQueueIndex], ...payload, _id: activeEditId };
                        return next;
                    });
                } else if (editorMode === 'revisor' && hasRevisorQueue) {
                    const nextIndex = revisorQueueIndex + 1;
                    if (nextIndex < revisorQueue.length) {
                        navigateRevisorQueue(nextIndex);
                    } else {
                        onShowToast('Última questão da fila revisada!', 'info');
                    }
                } else {
                    onSaved();
                }
            } else {
                const created = await questionService.createQuestion(payload);
                addQuestionToSession(created);
                onShowToast('Questão criada', 'success');
                onSaved();
            }
        } catch (e: any) { onShowToast(e.message || 'Erro ao salvar', 'error'); }
    };

    const handleDeleteQuestion = async () => {
        if (!activeEditId) return;
        if (!window.confirm('Tem certeza que deseja excluir esta questão? Esta ação não pode ser desfeita.')) return;
        try {
            await questionService.deleteQuestion(activeEditId);
            onShowToast('Questão excluída', 'success');
            if (editorMode === 'revisor' && hasRevisorQueue) {
                const newQueue = revisorQueue.filter((_: any, i: number) => i !== revisorQueueIndex);
                setRevisorQueue(newQueue);
                if (newQueue.length === 0) {
                    setActiveEditId(null);
                } else {
                    const nextIndex = Math.min(revisorQueueIndex, newQueue.length - 1);
                    setRevisorQueueIndex(nextIndex);
                    const q = newQueue[nextIndex];
                    setActiveEditId(q._id);
                    applyQuestionData(q);
                    loadQuestionAnalysesFor(q._id);
                }
            } else if (editorMode === 'edit' && hasEditQueue) {
                const newQueue = editQueue.filter((_: any, i: number) => i !== editQueueIndex);
                setEditQueue(newQueue);
                if (newQueue.length === 0) {
                    onSaved();
                } else {
                    const nextIndex = Math.min(editQueueIndex, newQueue.length - 1);
                    setEditQueueIndex(nextIndex);
                    const q = newQueue[nextIndex];
                    setActiveEditId(q._id);
                    applyQuestionData(q);
                }
            } else {
                onSaved();
            }
        } catch (e: any) { onShowToast(e.message || 'Erro ao excluir questão', 'error'); }
    };

    const embedUrl = getYoutubeEmbedUrl(explanationVideoUrl);
    const isExternalEdit = !!editingQuestionId;

    // Pré-computados para o modo Revisor
    const diffVotes = { facil: 0, medio: 0, dificil: 0 };
    const isCorrectCounts = { sim: 0, nao: 0 };
    const canPublishCounts = { sim: 0, nao: 0 };
    const shouldDeleteCounts = { sim: 0, nao: 0 };
    const shouldRedoCounts = { totalmente: 0, parcialmente: 0, nao: 0 };
    questionAnalyses.forEach(a => {
        if (a.suggestedDifficulty === 'facil') diffVotes.facil++;
        else if (a.suggestedDifficulty === 'medio') diffVotes.medio++;
        else if (a.suggestedDifficulty === 'dificil') diffVotes.dificil++;
        if (a.isCorrect === true) isCorrectCounts.sim++;
        else if (a.isCorrect === false) isCorrectCounts.nao++;
        if (a.canPublish === true) canPublishCounts.sim++;
        else if (a.canPublish === false) canPublishCounts.nao++;
        if (a.shouldDelete === true) shouldDeleteCounts.sim++;
        else if (a.shouldDelete === false) shouldDeleteCounts.nao++;
        if (a.shouldRedo === 'totalmente') shouldRedoCounts.totalmente++;
        else if (a.shouldRedo === 'parcialmente') shouldRedoCounts.parcialmente++;
        else if (a.shouldRedo === 'nao') shouldRedoCounts.nao++;
    });
    const catSuggestions: Array<{ reviserName: string; cat: any }> = questionAnalyses
        .filter(a => a.suggestedCategory?.level1)
        .map(a => ({ reviserName: a.reviserName, cat: a.suggestedCategory }));

    /* ════════════════════════════════════════════════════════
       RENDER
    ════════════════════════════════════════════════════════ */
    return (
        <div className="qft__wrapper">

            <ImageZoomModal
                imageUrl={zoomUrl}
                onClose={() => { setZoomUrl(null); setZoomScale(1); }}
                zoomScale={zoomScale}
                onZoomChange={setZoomScale}
            />

            {/* ══════════════════════════════════════════════
                NAVIGATOR BAR
            ══════════════════════════════════════════════ */}
            <div className="qft__nav">
                {/* Mode tabs */}
                <div className="qft__nav-modes">
                    <button
                        className={`qft__nav-mode-btn ${editorMode === 'create' ? 'active' : ''}`}
                        onClick={() => switchMode('create')}
                    >
                        <Plus size={14} /> Nova Questão
                    </button>
                    <button
                        className={`qft__nav-mode-btn ${editorMode === 'edit' ? 'active' : ''}`}
                        onClick={() => switchMode('edit')}
                    >
                        <AlertCircle size={14} /> Editar Questões
                    </button>
                    <button
                        className={`qft__nav-mode-btn ${editorMode === 'import' ? 'active' : ''}`}
                        onClick={() => switchMode('import')}
                    >
                        <FileJson size={14} /> Importar JSON
                    </button>
                    <button
                        className={`qft__nav-mode-btn qft__nav-mode-btn--prova ${editorMode === 'prova' ? 'active' : ''}`}
                        onClick={() => switchMode('prova')}
                    >
                        <BookLock size={14} /> Prova
                    </button>
                </div>

                {/* ── EDIT MODE PANEL ── */}
                {editorMode === 'edit' && (
                    <div className="qft__nav-panel">
                        <div className="qft__nav-filters">
                            <div className="qft__sel-wrap qft__nav-sel">
                                <select
                                    className="qft__select"
                                    value={navStatus}
                                    onChange={e => setNavStatus(e.target.value)}
                                >
                                    <option value="">Todos os status</option>
                                    <option value="rascunho">Rascunho</option>
                                    <option value="analise">Em Análise</option>
                                    <option value="publicado">Publicado</option>
                                </select>
                                <ChevronDown size={11} className="qft__sel-icon" />
                            </div>
                            <div className="qft__sel-wrap qft__nav-sel">
                                <select
                                    className="qft__select"
                                    value={navDifficulty}
                                    onChange={e => setNavDifficulty(e.target.value)}
                                >
                                    <option value="">Todas dificuldades</option>
                                    <option value="facil">Fácil</option>
                                    <option value="medio">Médio</option>
                                    <option value="dificil">Difícil</option>
                                </select>
                                <ChevronDown size={11} className="qft__sel-icon" />
                            </div>
                            <div className="qft__sel-wrap qft__nav-sel">
                                <select
                                    className="qft__select"
                                    value={navCat1}
                                    onChange={e => { setNavCat1(e.target.value); setNavCat2(''); }}
                                >
                                    <option value="">Todas as categorias</option>
                                    {allCategories.filter(c => c.level === 1).map(c => (
                                        <option key={c._id} value={c.name}>{c.name}</option>
                                    ))}
                                </select>
                                <ChevronDown size={11} className="qft__sel-icon" />
                            </div>
                            {navL2Opts.length > 0 && (
                                <div className="qft__sel-wrap qft__nav-sel">
                                    <select
                                        className="qft__select"
                                        value={navCat2}
                                        onChange={e => setNavCat2(e.target.value)}
                                    >
                                        <option value="">Todos os assuntos</option>
                                        {navL2Opts.map(c => (
                                            <option key={c._id} value={c.name}>{c.name}</option>
                                        ))}
                                    </select>
                                    <ChevronDown size={11} className="qft__sel-icon" />
                                </div>
                            )}
                            <div className="qft__sel-wrap qft__nav-sel">
                                <select
                                    className="qft__select"
                                    value={navPreset}
                                    onChange={e => setNavPreset(e.target.value)}
                                >
                                    <option value="">Todas as questões</option>
                                    <option value="desta-sessao">
                                        Desta sessão{sessionQuestions.length > 0 ? ` (${sessionQuestions.length})` : ''}
                                    </option>
                                    <option value="hoje">Criadas hoje</option>
                                    <option value="sem-resolucao">Sem resolução</option>
                                    <option value="sem-categoria">Sem categoria</option>
                                    <option value="publicadas-hoje">Publicadas hoje</option>
                                    <option value="publicadas-ontem">Publicadas ontem</option>
                                </select>
                                <ChevronDown size={11} className="qft__sel-icon" />
                            </div>
                            <button
                                className="qft__nav-search-btn"
                                onClick={loadEditQueue}
                                disabled={editQueueLoading}
                            >
                                {editQueueLoading
                                    ? <Loader2 size={14} className="qft__spin" />
                                    : <RefreshCw size={14} />}
                                {editQueueLoading ? 'Carregando...' : 'Buscar'}
                            </button>
                        </div>

                        {hasEditQueue ? (
                            <div className="qft__nav-navigation">
                                <button
                                    className="qft__nav-arrow"
                                    onClick={() => navigateEditQueue(editQueueIndex - 1)}
                                    disabled={editQueueIndex === 0}
                                    title="Anterior"
                                >
                                    <ChevronLeft size={18} />
                                </button>
                                <span className="qft__nav-counter">
                                    Q{editQueueIndex + 1} de {editQueue.length}
                                </span>
                                <button
                                    className="qft__nav-arrow"
                                    onClick={() => navigateEditQueue(editQueueIndex + 1)}
                                    disabled={editQueueIndex === editQueue.length - 1}
                                    title="Próxima"
                                >
                                    <ChevronRight size={18} />
                                </button>
                                <button className="qft__nav-copy-btn" onClick={copyQuestion} title="Copiar questão">
                                    <Copy size={13} /> Copiar
                                </button>
                                <button className="qft__nav-copy-btn qft__nav-copy-btn--res" onClick={copyQuestionWithResolution} title="Copiar com resolução">
                                    <Copy size={13} /> Copiar c/ resolução
                                </button>
                                {isExternalEdit && !hasEditQueue && (
                                    <button className="qft__edit-cancel" onClick={onCancel}>
                                        <X size={13} /> Cancelar
                                    </button>
                                )}
                            </div>
                        ) : (
                            <div className="qft__nav-empty">
                                {isExternalEdit ? (
                                    <>
                                        <span>Editando questão específica — use filtros + Buscar para navegar entre questões</span>
                                        <button className="qft__edit-cancel" onClick={onCancel}>
                                            <X size={13} /> Cancelar edição
                                        </button>
                                    </>
                                ) : (
                                    <span>Selecione os filtros e clique em <strong>Buscar</strong> para navegar entre questões</span>
                                )}
                            </div>
                        )}
                    </div>
                )}

                {/* ── REVISOR MODE PANEL ── */}
                {editorMode === 'revisor' && (
                    <div className="qft__nav-panel">
                        <div className="qft__nav-filters">
                            <div className="qft__sel-wrap qft__nav-sel">
                                <select
                                    className="qft__select"
                                    value={revisorFilterRevisor}
                                    onChange={e => setRevisorFilterRevisor(e.target.value)}
                                >
                                    <option value="">Todos os revisores</option>
                                    {revisoresList.map(r => (
                                        <option key={r._id} value={r._id}>{r.username}</option>
                                    ))}
                                </select>
                                <ChevronDown size={11} className="qft__sel-icon" />
                            </div>
                            <div className="qft__sel-wrap qft__nav-sel">
                                <select
                                    className="qft__select"
                                    value={revisorFilterCat1}
                                    onChange={e => { setRevisorFilterCat1(e.target.value); setRevisorFilterCat2(''); }}
                                >
                                    <option value="">Todas as categorias</option>
                                    {allCategories.filter(c => c.level === 1).map(c => (
                                        <option key={c._id} value={c.name}>{c.name}</option>
                                    ))}
                                </select>
                                <ChevronDown size={11} className="qft__sel-icon" />
                            </div>
                            {revisorL2Opts.length > 0 && (
                                <div className="qft__sel-wrap qft__nav-sel">
                                    <select
                                        className="qft__select"
                                        value={revisorFilterCat2}
                                        onChange={e => setRevisorFilterCat2(e.target.value)}
                                    >
                                        <option value="">Todos os assuntos</option>
                                        {revisorL2Opts.map(c => (
                                            <option key={c._id} value={c.name}>{c.name}</option>
                                        ))}
                                    </select>
                                    <ChevronDown size={11} className="qft__sel-icon" />
                                </div>
                            )}
                            <div className="qft__sel-wrap qft__nav-sel">
                                <select
                                    className="qft__select"
                                    value={revisorFilterAnalysis}
                                    onChange={e => setRevisorFilterAnalysis(e.target.value as 'com' | 'sem' | '')}
                                >
                                    <option value="">Todas</option>
                                    <option value="com">Com análise</option>
                                    <option value="sem">Sem análise</option>
                                </select>
                                <ChevronDown size={11} className="qft__sel-icon" />
                            </div>
                            <button
                                className="qft__nav-search-btn"
                                onClick={loadRevisorQueue}
                                disabled={revisorQueueLoading}
                            >
                                {revisorQueueLoading
                                    ? <Loader2 size={14} className="qft__spin" />
                                    : <RefreshCw size={14} />}
                                {revisorQueueLoading ? 'Carregando...' : 'Buscar'}
                            </button>
                        </div>

                        {hasRevisorQueue ? (
                            <div className="qft__nav-navigation">
                                <button
                                    className="qft__nav-arrow"
                                    onClick={() => navigateRevisorQueue(revisorQueueIndex - 1)}
                                    disabled={revisorQueueIndex === 0}
                                    title="Anterior"
                                >
                                    <ChevronLeft size={18} />
                                </button>
                                <span className="qft__nav-counter">
                                    Q{revisorQueueIndex + 1} de {revisorQueue.length}
                                    {revisorQueue[revisorQueueIndex]?.analysisCount > 0 && (
                                        <span className="qft__rev-analysis-badge">
                                            {revisorQueue[revisorQueueIndex].analysisCount} análise(s)
                                        </span>
                                    )}
                                </span>
                                <button
                                    className="qft__nav-arrow"
                                    onClick={() => navigateRevisorQueue(revisorQueueIndex + 1)}
                                    disabled={revisorQueueIndex === revisorQueue.length - 1}
                                    title="Próxima"
                                >
                                    <ChevronRight size={18} />
                                </button>
                                <button className="qft__nav-copy-btn" onClick={copyQuestion}>
                                    <Copy size={13} /> Copiar
                                </button>
                                <button className="qft__nav-copy-btn qft__nav-copy-btn--res" onClick={copyQuestionWithResolution} title="Copiar com resolução">
                                    <Copy size={13} /> Copiar c/ resolução
                                </button>
                            </div>
                        ) : (
                            <div className="qft__nav-empty">
                                <span>Selecione os filtros e clique em <strong>Buscar</strong> para revisar questões em análise</span>
                            </div>
                        )}
                    </div>
                )}

                {/* ── IMPORT MODE PANEL ── */}
                {editorMode === 'import' && (
                    <div className="qft__nav-panel">
                        {!hasImportQueue ? (
                            <div className="qft__nav-import-input">
                                <button
                                    className="qft__nav-upload-btn"
                                    onClick={() => navFileInputRef.current?.click()}
                                >
                                    <Upload size={14} /> Carregar arquivo .json
                                </button>
                                <input
                                    ref={navFileInputRef}
                                    type="file"
                                    accept=".json"
                                    style={{ display: 'none' }}
                                    onChange={e => {
                                        const file = e.target.files?.[0];
                                        if (!file) return;
                                        const reader = new FileReader();
                                        reader.onload = ev => processImportJson(ev.target?.result as string);
                                        reader.readAsText(file);
                                        e.target.value = '';
                                    }}
                                />
                                {jsonParseError && (
                                    <div className="qft__nav-parse-error">
                                        <AlertCircle size={13} /> {jsonParseError}
                                    </div>
                                )}
                            </div>
                        ) : (
                            <div className="qft__nav-import-queue">
                                <div className="qft__nav-import-top">
                                    <div className="qft__nav-import-info">
                                        <FileJson size={15} />
                                        <strong>{importQueue.length}</strong> questões na fila
                                    </div>
                                    <div className="qft__nav-bulk-status">
                                        <span className="qft__nav-bulk-label">Definir todas:</span>
                                        {(['rascunho', 'analise', 'publicado'] as QuestionStatus[]).map(s => (
                                            <button
                                                key={s}
                                                className={`qft__nav-bulk-btn qft__nav-bulk-btn--${s}`}
                                                onClick={() => setAllImportStatus(s)}
                                            >
                                                {s === 'rascunho' ? 'Rascunho' : s === 'analise' ? 'Análise' : 'Publicado'}
                                            </button>
                                        ))}
                                    </div>
                                    <button
                                        className="qft__edit-cancel"
                                        onClick={() => { setImportQueue([]); setImportIndex(0); resetForm(); setJsonInput(''); }}
                                    >
                                        <X size={13} /> Cancelar
                                    </button>
                                </div>

                                <div className="qft__nav-import-controls">
                                    <div className="qft__nav-navigation">
                                        <button
                                            className="qft__nav-arrow"
                                            onClick={() => navigateImportQueue(importIndex - 1)}
                                            disabled={importIndex === 0}
                                        >
                                            <ChevronLeft size={18} />
                                        </button>
                                        <span className="qft__nav-counter">
                                            Q{importIndex + 1} de {importQueue.length}
                                        </span>
                                        <button
                                            className="qft__nav-arrow"
                                            onClick={() => navigateImportQueue(importIndex + 1)}
                                            disabled={importIndex === importQueue.length - 1}
                                        >
                                            <ChevronRight size={18} />
                                        </button>
                                    </div>
                                    <div className="qft__nav-import-actions">
                                        <button
                                            className="qft__nav-action-btn qft__nav-action-btn--discard"
                                            onClick={() => discardImportQuestion(importIndex)}
                                            disabled={importImporting}
                                        >
                                            <Trash2 size={13} /> Descartar
                                        </button>
                                        <button
                                            className="qft__nav-action-btn qft__nav-action-btn--import"
                                            onClick={importSingle}
                                            disabled={importImporting}
                                        >
                                            {importImporting ? <Loader2 size={13} className="qft__spin" /> : <Download size={13} />}
                                            Importar esta
                                        </button>
                                        <button
                                            className="qft__nav-action-btn qft__nav-action-btn--import-all"
                                            onClick={importAll}
                                            disabled={importImporting}
                                        >
                                            {importImporting ? <Loader2 size={13} className="qft__spin" /> : <Check size={13} />}
                                            Importar todas ({importQueue.length})
                                        </button>
                                        <button className="qft__nav-copy-btn" onClick={copyQuestion}>
                                            <Copy size={13} /> Copiar
                                        </button>
                                        <button className="qft__nav-copy-btn qft__nav-copy-btn--res" onClick={copyQuestionWithResolution} title="Copiar com resolução">
                                            <Copy size={13} /> Copiar c/ resolução
                                        </button>
                                    </div>
                                </div>

                                <div className="qft__nav-dots">
                                    {importQueue.map((item, i) => (
                                        <button
                                            key={i}
                                            className={`qft__nav-dot qft__nav-dot--${item.status} ${i === importIndex ? 'active' : ''}`}
                                            onClick={() => navigateImportQueue(i)}
                                            title={`Q${i + 1}: ${item.status}`}
                                        />
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                )}
                {/* ── PROVA / SIMULADO MODE PANEL ── */}
                {isExamMode && (
                    <div className="qft__nav-panel">
                        {/* Metadados do exame */}
                        <div className="qft__exam-form">
                            <div className="qft__exam-form-row">
                                <div className="qft__field">
                                    <label className="qft__lbl">
                                        {editorMode === 'prova' ? 'Nome da Prova' : 'Nome do Simulado'} <span className="qft__req">*</span>
                                    </label>
                                    <input
                                        className="qft__input"
                                        value={examName}
                                        onChange={e => setExamName(e.target.value)}
                                        placeholder={editorMode === 'prova' ? 'Ex: FAMEMA 2024' : 'Ex: Simulado Cardiologia #1'}
                                    />
                                </div>
                                {editorMode === 'prova' && (
                                    <div className="qft__field">
                                        <label className="qft__lbl">Instituição <span className="qft__req">*</span></label>
                                        <input
                                            className="qft__input"
                                            value={examInstitution}
                                            onChange={e => setExamInstitution(e.target.value)}
                                            placeholder="Ex: FAMEMA, REVALIDA"
                                        />
                                    </div>
                                )}
                                <div className="qft__field qft__field--narrow">
                                    <label className="qft__lbl">Ano</label>
                                    <input
                                        type="number"
                                        className="qft__input"
                                        value={examYear}
                                        onChange={e => setExamYear(e.target.value === '' ? '' : Number(e.target.value))}
                                        placeholder="2024"
                                        min={2000}
                                        max={2100}
                                    />
                                </div>
                                <div className="qft__field">
                                    <label className="qft__lbl">Área</label>
                                    <input
                                        className="qft__input"
                                        value={examArea}
                                        onChange={e => setExamArea(e.target.value)}
                                        placeholder="Ex: Medicina"
                                    />
                                </div>
                                <div className="qft__field">
                                    <label className="qft__lbl">Nível</label>
                                    <input
                                        className="qft__input"
                                        value={examLevel}
                                        onChange={e => setExamLevel(e.target.value)}
                                        placeholder="Ex: Residência Médica"
                                    />
                                </div>
                                <div className="qft__field qft__field--narrow">
                                    <label className="qft__lbl">Duração (min)</label>
                                    <input
                                        type="number"
                                        className="qft__input"
                                        value={examDuration}
                                        onChange={e => setExamDuration(Number(e.target.value))}
                                        min={1}
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Fila de questões */}
                        {!hasImportQueue ? (
                            <div className="qft__nav-import-input">
                                <button
                                    className="qft__nav-upload-btn"
                                    onClick={() => navFileInputRef.current?.click()}
                                >
                                    <Upload size={14} /> Carregar questões (.json)
                                </button>
                                <input
                                    ref={navFileInputRef}
                                    type="file"
                                    accept=".json"
                                    style={{ display: 'none' }}
                                    onChange={e => {
                                        const file = e.target.files?.[0];
                                        if (!file) return;
                                        const reader = new FileReader();
                                        reader.onload = ev => processImportJson(ev.target?.result as string);
                                        reader.readAsText(file);
                                        e.target.value = '';
                                    }}
                                />
                                {jsonParseError && (
                                    <div className="qft__nav-parse-error">
                                        <AlertCircle size={13} /> {jsonParseError}
                                    </div>
                                )}
                            </div>
                        ) : (
                            <div className="qft__nav-import-queue">
                                <div className="qft__nav-import-top">
                                    <div className="qft__nav-import-info">
                                        <FileJson size={15} />
                                        <strong>{importQueue.length}</strong> questões na fila
                                    </div>
                                    <div className="qft__nav-bulk-status">
                                        <span className="qft__nav-bulk-label">Definir todas:</span>
                                        {((editorMode === 'simulado' ? ['simulado', 'publicado', 'rascunho'] : ['prova', 'publicado', 'rascunho']) as QuestionStatus[]).map(s => (
                                            <button
                                                key={s}
                                                className={`qft__nav-bulk-btn qft__nav-bulk-btn--${s}`}
                                                onClick={() => setAllImportStatus(s)}
                                            >
                                                {s === 'prova' ? 'Excl. Prova' : s === 'simulado' ? 'Excl. Simulado' : s === 'publicado' ? 'Publicado' : 'Rascunho'}
                                            </button>
                                        ))}
                                    </div>
                                    <button
                                        className="qft__edit-cancel"
                                        onClick={() => { setImportQueue([]); setImportIndex(0); resetForm(); setJsonInput(''); }}
                                    >
                                        <X size={13} /> Cancelar
                                    </button>
                                </div>

                                <div className="qft__nav-import-controls">
                                    <div className="qft__nav-navigation">
                                        <button
                                            className="qft__nav-arrow"
                                            onClick={() => navigateImportQueue(importIndex - 1)}
                                            disabled={importIndex === 0}
                                        >
                                            <ChevronLeft size={18} />
                                        </button>
                                        <span className="qft__nav-counter">
                                            Q{importIndex + 1} de {importQueue.length}
                                        </span>
                                        <button
                                            className="qft__nav-arrow"
                                            onClick={() => navigateImportQueue(importIndex + 1)}
                                            disabled={importIndex === importQueue.length - 1}
                                        >
                                            <ChevronRight size={18} />
                                        </button>
                                    </div>
                                    <div className="qft__nav-import-actions">
                                        <button
                                            className="qft__nav-action-btn qft__nav-action-btn--discard"
                                            onClick={() => discardImportQuestion(importIndex)}
                                            disabled={examImporting}
                                        >
                                            <Trash2 size={13} /> Descartar
                                        </button>
                                        <button className="qft__nav-copy-btn" onClick={copyQuestion}>
                                            <Copy size={13} /> Copiar
                                        </button>
                                        <button className="qft__nav-copy-btn qft__nav-copy-btn--res" onClick={copyQuestionWithResolution}>
                                            <Copy size={13} /> Copiar c/ resolução
                                        </button>
                                    </div>
                                </div>

                                <div className="qft__nav-dots">
                                    {importQueue.map((item, i) => (
                                        <button
                                            key={i}
                                            className={`qft__nav-dot qft__nav-dot--${item.status} ${i === importIndex ? 'active' : ''}`}
                                            onClick={() => navigateImportQueue(i)}
                                            title={`Q${i + 1}: ${item.status}`}
                                        />
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* ══════════════════════════════════════════════
                CARD 1 — ENUNCIADO + ALTERNATIVAS
            ══════════════════════════════════════════════ */}
            <div className="qft__card">
                <section className="qft__section">
                    <div className="qft__section-header">
                        <span className="qft__section-title">Enunciado <span className="qft__req">*</span></span>
                        <label
                            className={`qft__img-btn ${statementImageUploading ? 'qft__img-btn--loading' : ''}`}
                            title="Inserir imagem no enunciado"
                        >
                            {statementImageUploading ? <Loader2 size={13} className="qft__spin" /> : null}
                            <span>{statementImageUploading ? 'Enviando...' : 'Imagem externa'}</span>
                            <input
                                type="file"
                                accept="image/*"
                                onChange={handleStatementImageUpload}
                                style={{ display: 'none' }}
                                disabled={statementImageUploading}
                            />
                        </label>
                    </div>

                    <QuestionRichEditor
                        key={`stmt-${editorKey}`}
                        value={statement}
                        onChange={setStatement}
                        placeholder="Digite o enunciado da questão... (use a toolbar para formatar)"
                    />

                    {editorMode === 'revisor' && questionAnalyses.filter(a => a.suggestedStatement).length > 0 && (
                        <div className="qft__rev-suggestions">
                            <span className="qft__rev-suggestions-label">Enunciados sugeridos:</span>
                            {questionAnalyses.filter(a => a.suggestedStatement).map((a, i) => (
                                <div key={i} className="qft__rev-suggestion-item">
                                    <div className="qft__rev-suggestion-meta">
                                        <span className="qft__rev-revisor-name">{a.reviserName}</span>
                                        <span className="qft__rev-suggestion-date">{new Date(a.createdAt).toLocaleDateString('pt-BR')}</span>
                                    </div>
                                    <div className="qft__rev-suggestion-text">{a.suggestedStatement}</div>
                                    <button
                                        className="qft__rev-accept-btn"
                                        onClick={() => acceptSuggestedStatement(a.suggestedStatement)}
                                    >
                                        <Check size={12} /> Aceitar sugestão
                                    </button>
                                </div>
                            ))}
                        </div>
                    )}

                    {statementImage && (
                        <>
                            <div className="qft__img-separator"><span>Imagem destacada</span></div>
                            <div className="qft__stmt-img-wrap">
                                <img
                                    src={statementImage}
                                    alt=""
                                    className="qft__stmt-img"
                                    onClick={() => { setZoomUrl(statementImage); setZoomScale(1); }}
                                    title="Clique para ampliar"
                                />
                                <button className="qft__stmt-img-del" onClick={() => setStatementImage('')} title="Remover">
                                    <X size={12} />
                                </button>
                            </div>
                        </>
                    )}
                </section>

                <section className="qft__section">
                    <div className="qft__section-header">
                        <span className="qft__section-title">Alternativas</span>
                        <button
                            type="button"
                            className="qft__paste-btn"
                            onClick={() => setShowPasteModal(true)}
                        >
                            <ClipboardPaste size={13} /> Colar múltiplas
                        </button>
                    </div>
                    <div className="qft__alts">
                        {alternatives.map((alt: any, index: number) => {
                            const altSuggestions = editorMode === 'revisor'
                                ? questionAnalyses
                                    .filter(a => a.suggestedAlternatives?.some((sa: any) => sa.letter === alt.letter))
                                    .map(a => ({
                                        reviserName: a.reviserName,
                                        text: a.suggestedAlternatives.find((sa: any) => sa.letter === alt.letter)?.text || ''
                                    }))
                                : [];
                            return (
                                <React.Fragment key={alt.letter}>
                                    <AltRow
                                        alt={alt}
                                        index={index}
                                        total={alternatives.length}
                                        onChangeText={(i, value) => handleAlternativeChange(i, 'text', value)}
                                        onSelectCorrect={(i) => handleAlternativeChange(i, 'isCorrect', true)}
                                        onRemove={handleRemoveAlternative}
                                    />
                                    {altSuggestions.length > 0 && (
                                        <div className="qft__rev-alt-suggestions">
                                            {altSuggestions.map((s, si) => (
                                                <div key={si} className="qft__rev-alt-suggestion">
                                                    <span className="qft__rev-alt-revisor">{s.reviserName}:</span>
                                                    <span className="qft__rev-alt-text">{s.text}</span>
                                                    <button
                                                        className="qft__rev-accept-btn qft__rev-accept-btn--sm"
                                                        onClick={() => acceptSuggestedAlt(alt.letter, s.text)}
                                                    >
                                                        <Check size={11} /> Aceitar
                                                    </button>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </React.Fragment>
                            );
                        })}
                        {alternatives.length < 5 && (
                            <button className="qft__add-alt" onClick={handleAddAlternative}>
                                <Plus size={13} /> Adicionar alternativa
                            </button>
                        )}
                    </div>
                </section>
            </div>

            {/* ══════════════════════════════════════════════
                CARD 2 — CATEGORIAS + DIFICULDADE
            ══════════════════════════════════════════════ */}
            <div className="qft__card">
                <section className="qft__section">
                    <div className="qft__section-header">
                        <span className="qft__section-title">
                            Categorias{editorMode !== 'import' && editorMode !== 'prova' && editorMode !== 'simulado' && <span className="qft__req"> *</span>}
                        </span>
                        <span className="qft__hint">Selecione uma ou mais</span>
                    </div>

                    {selectedCategories.length > 0 && (
                        <div className="qft__chips">
                            {selectedCategories.map(cat => (
                                <span key={cat.id} className="qft__chip">
                                    {cat.breadcrumb}
                                    <button
                                        className="qft__chip-x"
                                        onClick={() => setSelectedCategories(prev => prev.filter(c => c.id !== cat.id))}
                                    >
                                        <X size={10} />
                                    </button>
                                </span>
                            ))}
                        </div>
                    )}

                    <div className="qft__cat-grid">
                        {[
                            { label: 'Campo de Estudo', opts: level1Opts, val: sel1, set: (v: string) => { setSel1(v); setSel2(''); setSel3(''); setSel4(''); }, disabled: false },
                            { label: 'Assunto', opts: level2Opts, val: sel2, set: (v: string) => { setSel2(v); setSel3(''); setSel4(''); }, disabled: !sel1 },
                            { label: 'Tema', opts: level3Opts, val: sel3, set: (v: string) => { setSel3(v); setSel4(''); }, disabled: !sel2 },
                            { label: 'Subtema', opts: level4Opts, val: sel4, set: (v: string) => setSel4(v), disabled: !sel3 },
                        ].map(({ label, opts, val, set, disabled }) => (
                            <div key={label} className={`qft__cat-col${disabled ? ' qft__cat-col--off' : ''}`}>
                                <span className="qft__cat-lbl">{label}</span>
                                <div className="qft__sel-wrap">
                                    <select
                                        className={`qft__select${val ? ' qft__select--on' : ''}`}
                                        value={val}
                                        onChange={e => set(e.target.value)}
                                        disabled={disabled || opts.length === 0}
                                    >
                                        <option value="">{opts.length ? 'Selecionar...' : '—'}</option>
                                        {opts.map(c => <option key={c._id} value={c._id}>{c.name}</option>)}
                                    </select>
                                    <ChevronDown size={11} className="qft__sel-icon" />
                                </div>
                            </div>
                        ))}
                    </div>

                    <div className="qft__cat-actions">
                        <button className="qft__add-cat" onClick={handleAddCategory} disabled={!tempFinalId}>
                            <Plus size={13} /> Adicionar categoria
                        </button>
                        <button className="qft__new-cat-btn" onClick={() => setShowCreateCategory(true)}>
                            <Plus size={13} /> Nova categoria
                        </button>
                    </div>

                    {editorMode === 'revisor' && catSuggestions.length > 0 && (
                        <div className="qft__rev-suggestions" style={{ marginTop: 12 }}>
                            <span className="qft__rev-suggestions-label">Categorias sugeridas:</span>
                            {catSuggestions.map((s, i) => (
                                <div key={i} className="qft__rev-suggestion-item">
                                    <div className="qft__rev-suggestion-meta">
                                        <span className="qft__rev-revisor-name">{s.reviserName}</span>
                                    </div>
                                    <div className="qft__rev-suggestion-text">
                                        {[s.cat.level1, s.cat.level2, s.cat.level3, s.cat.level4].filter(Boolean).join(' › ')}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </section>

                <section className="qft__section qft__section--slim">
                    <div className="qft__rev-diff-area">
                        <div className="qft__field" style={{ maxWidth: 220 }}>
                            <label className="qft__lbl">Dificuldade</label>
                            <div className="qft__sel-wrap">
                                <select className="qft__select" value={difficulty} onChange={e => setDifficulty(e.target.value as any)}>
                                    <option value="">Sem definição</option>
                                    <option value="facil">Fácil</option>
                                    <option value="medio">Médio</option>
                                    <option value="dificil">Difícil</option>
                                </select>
                                <ChevronDown size={11} className="qft__sel-icon" />
                            </div>
                        </div>
                        {editorMode === 'revisor' && (diffVotes.facil + diffVotes.medio + diffVotes.dificil) > 0 && (
                            <div className="qft__rev-diff-votes">
                                <span className="qft__rev-diff-label">Sugestões dos revisores:</span>
                                <div className="qft__analysis-stat-row">
                                    {diffVotes.facil > 0 && <span className="qft__av qft__av--facil">Fácil {diffVotes.facil}</span>}
                                    {diffVotes.medio > 0 && <span className="qft__av qft__av--medio">Médio {diffVotes.medio}</span>}
                                    {diffVotes.dificil > 0 && <span className="qft__av qft__av--dificil">Difícil {diffVotes.dificil}</span>}
                                </div>
                                <div className="qft__rev-diff-detail">
                                    {questionAnalyses.filter(a => a.suggestedDifficulty).map((a, i) => (
                                        <span key={i} className="qft__rev-diff-revisor">
                                            {a.reviserName}: {a.suggestedDifficulty === 'facil' ? 'Fácil' : a.suggestedDifficulty === 'medio' ? 'Médio' : 'Difícil'}
                                        </span>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                </section>
            </div>

            {/* ══════════════════════════════════════════════
                CARD 3 — RESOLUÇÃO + FOOTER DINÂMICO
            ══════════════════════════════════════════════ */}
            <div className="qft__card">
                <section className="qft__section">
                    <div className="qft__section-header">
                        <span className="qft__section-title">Resolução</span>
                        <span className="qft__hint">Opcional — preencha após análise</span>
                    </div>

                    <div className="qft__field">
                        <label className="qft__lbl">Texto explicativo</label>
                        <QuestionRichEditor
                            key={`expl-${editorKey}`}
                            value={explanationText}
                            onChange={setExplanationText}
                            placeholder="Escreva a explicação da resposta correta..."
                        />
                    </div>

                    <div className="qft__field">
                        <label className="qft__lbl">Vídeo no YouTube</label>
                        <input
                            type="text" className="qft__input" value={explanationVideoUrl}
                            onChange={e => setExplanationVideoUrl(e.target.value)}
                            placeholder="https://www.youtube.com/watch?v=..."
                        />
                    </div>

                    {embedUrl && (
                        <div className="qft__yt-wrap">
                            <iframe
                                src={embedUrl} className="qft__yt-frame" title="Vídeo explicativo"
                                allowFullScreen
                                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                            />
                        </div>
                    )}
                </section>

                {/* ── Footer: muda conforme o modo ── */}
                {editorMode === 'import' && hasImportQueue ? (
                    <div className="qft__footer qft__footer--import">
                        <div className="qft__import-status-tabs">
                            {(['rascunho', 'analise', 'publicado'] as QuestionStatus[]).map(s => (
                                <button
                                    key={s}
                                    className={`qft__import-status-tab qft__import-status-tab--${s} ${status === s ? 'active' : ''}`}
                                    onClick={() => setImportItemStatus(s)}
                                >
                                    {s === 'rascunho' ? 'Rascunho' : s === 'analise' ? 'Em Análise' : 'Publicar'}
                                </button>
                            ))}
                        </div>
                        <button className="qft__nav-copy-btn" onClick={copyQuestion}>
                            <Copy size={13} /> Copiar
                        </button>
                        <button className="qft__nav-copy-btn qft__nav-copy-btn--res" onClick={copyQuestionWithResolution} title="Copiar com resolução">
                            <Copy size={13} /> Copiar c/ resolução
                        </button>
                        <button className="qft__save-btn" onClick={importSingle} disabled={importImporting}>
                            {importImporting ? <Loader2 size={15} className="qft__spin" /> : <Download size={15} />}
                            Importar esta
                        </button>
                    </div>
                ) : isExamMode && hasImportQueue ? (
                    <div className="qft__footer qft__footer--import">
                        <div className="qft__import-status-tabs">
                            {((editorMode === 'simulado' ? ['simulado', 'publicado', 'rascunho'] : ['prova', 'publicado', 'rascunho']) as QuestionStatus[]).map(s => (
                                <button
                                    key={s}
                                    className={`qft__import-status-tab qft__import-status-tab--${s} ${status === s ? 'active' : ''}`}
                                    onClick={() => setImportItemStatus(s)}
                                >
                                    {s === 'prova' ? 'Exclusivo Prova' : s === 'simulado' ? 'Exclusivo Simulado' : s === 'publicado' ? 'Publicar' : 'Rascunho'}
                                </button>
                            ))}
                        </div>
                        <button className="qft__nav-copy-btn" onClick={copyQuestion}>
                            <Copy size={13} /> Copiar
                        </button>
                        <button className="qft__nav-copy-btn qft__nav-copy-btn--res" onClick={copyQuestionWithResolution} title="Copiar com resolução">
                            <Copy size={13} /> Copiar c/ resolução
                        </button>
                        <button className="qft__save-btn qft__save-btn--exam" onClick={importExamFinal} disabled={examImporting}>
                            {examImporting ? <Loader2 size={15} className="qft__spin" /> : <Upload size={15} />}
                            Importar {editorMode === 'prova' ? 'Prova' : 'Simulado'} ({importQueue.length})
                        </button>
                    </div>
                ) : (
                    <div className="qft__footer">
                        <div className="qft__sel-wrap qft__status-sel">
                            <select className="qft__select" value={status} onChange={e => setStatus(e.target.value as any)}>
                                <option value="rascunho">Rascunho</option>
                                <option value="analise">Em Análise</option>
                                <option value="publicado">Publicado</option>
                            </select>
                            <ChevronDown size={11} className="qft__sel-icon" />
                        </div>
                        {(editorMode === 'edit' || activeEditId) && (<>
                            <button className="qft__nav-copy-btn" onClick={copyQuestion} title="Copiar questão">
                                <Copy size={13} /> Copiar
                            </button>
                            <button className="qft__nav-copy-btn qft__nav-copy-btn--res" onClick={copyQuestionWithResolution} title="Copiar com resolução">
                                <Copy size={13} /> Copiar c/ resolução
                            </button>
                            <button className="qft__nav-copy-btn qft__nav-copy-btn--delete" onClick={handleDeleteQuestion} title="Excluir questão">
                                <Trash2 size={13} /> Excluir
                            </button>
                        </>)}
                        <button className="qft__save-btn" onClick={handleSave}>
                            <Save size={15} />
                            {activeEditId ? 'Atualizar Questão' : 'Criar Questão'}
                        </button>
                    </div>
                )}
            </div>

            {/* ══════════════════════════════════════════════
                CARD 4 — ANÁLISES (modo Revisor)
            ══════════════════════════════════════════════ */}
            {editorMode === 'revisor' && activeEditId && (
                <div className="qft__card">
                    <section className="qft__section">
                        <div className="qft__section-header">
                            <span className="qft__section-title">
                                <FlaskConical size={14} style={{ verticalAlign: 'middle', marginRight: 5 }} />
                                Análises
                                {questionAnalyses.length > 0 && (
                                    <span className="qft__rev-count-badge">{questionAnalyses.length}</span>
                                )}
                            </span>
                        </div>

                        {analysesLoading ? (
                            <div className="qft__analyses-loading">
                                <Loader2 size={15} className="qft__spin" /> Carregando...
                            </div>
                        ) : questionAnalyses.length === 0 ? (
                            <div className="qft__analyses-empty">Nenhuma análise enviada para esta questão.</div>
                        ) : (
                            <>
                                {/* ── Resumo de votos ── */}
                                <div className="qft__analysis-summary">
                                    {(isCorrectCounts.sim > 0 || isCorrectCounts.nao > 0) && (
                                        <div className="qft__analysis-stat">
                                            <span className="qft__analysis-stat-label">Correta?</span>
                                            <div className="qft__analysis-stat-row">
                                                {isCorrectCounts.sim > 0 && <span className="qft__av qft__av--correct">✓ {isCorrectCounts.sim}</span>}
                                                {isCorrectCounts.nao > 0 && <span className="qft__av qft__av--wrong">✗ {isCorrectCounts.nao}</span>}
                                            </div>
                                        </div>
                                    )}
                                    {(canPublishCounts.sim > 0 || canPublishCounts.nao > 0) && (
                                        <div className="qft__analysis-stat">
                                            <span className="qft__analysis-stat-label">Publicar?</span>
                                            <div className="qft__analysis-stat-row">
                                                {canPublishCounts.sim > 0 && <span className="qft__av qft__av--correct">✓ {canPublishCounts.sim}</span>}
                                                {canPublishCounts.nao > 0 && <span className="qft__av qft__av--wrong">✗ {canPublishCounts.nao}</span>}
                                            </div>
                                        </div>
                                    )}
                                    {shouldDeleteCounts.sim > 0 && (
                                        <div className="qft__analysis-stat">
                                            <span className="qft__analysis-stat-label">Excluir</span>
                                            <div className="qft__analysis-stat-row">
                                                <span className="qft__av qft__av--delete">Sim {shouldDeleteCounts.sim}</span>
                                                {shouldDeleteCounts.nao > 0 && <span className="qft__av qft__av--correct">Não {shouldDeleteCounts.nao}</span>}
                                            </div>
                                        </div>
                                    )}
                                    {(shouldRedoCounts.totalmente > 0 || shouldRedoCounts.parcialmente > 0) && (
                                        <div className="qft__analysis-stat">
                                            <span className="qft__analysis-stat-label">Refazer</span>
                                            <div className="qft__analysis-stat-row">
                                                {shouldRedoCounts.totalmente > 0 && <span className="qft__av qft__av--delete">Tot. {shouldRedoCounts.totalmente}</span>}
                                                {shouldRedoCounts.parcialmente > 0 && <span className="qft__av qft__av--warn">Parc. {shouldRedoCounts.parcialmente}</span>}
                                                {shouldRedoCounts.nao > 0 && <span className="qft__av qft__av--correct">Não {shouldRedoCounts.nao}</span>}
                                            </div>
                                        </div>
                                    )}
                                    {(diffVotes.facil + diffVotes.medio + diffVotes.dificil) > 0 && (
                                        <div className="qft__analysis-stat">
                                            <span className="qft__analysis-stat-label">Dificuldade</span>
                                            <div className="qft__analysis-stat-row">
                                                {diffVotes.facil > 0 && <span className="qft__av qft__av--facil">Fácil {diffVotes.facil}</span>}
                                                {diffVotes.medio > 0 && <span className="qft__av qft__av--medio">Médio {diffVotes.medio}</span>}
                                                {diffVotes.dificil > 0 && <span className="qft__av qft__av--dificil">Difícil {diffVotes.dificil}</span>}
                                            </div>
                                        </div>
                                    )}
                                </div>

                                {/* ── Lista individual com accordion ── */}
                                <div className="qft__analysis-list">
                                    {questionAnalyses.map((a, i) => {
                                        const collapsed = collapsedAnalyses.has(i);
                                        return (
                                            <div key={i} className="qft__analysis-item">
                                                <button
                                                    className="qft__analysis-toggle-btn"
                                                    onClick={() => toggleAnalysis(i)}
                                                    type="button"
                                                >
                                                    <span className="qft__rev-revisor-name">{a.reviserName}</span>
                                                    <span className="qft__rev-suggestion-date">{new Date(a.createdAt).toLocaleDateString('pt-BR')}</span>
                                                    <div className="qft__analysis-toggle-tags">
                                                        {a.isCorrect === true && <span className="qft__av qft__av--correct">✓</span>}
                                                        {a.isCorrect === false && <span className="qft__av qft__av--wrong">✗</span>}
                                                        {a.canPublish === true && <span className="qft__av qft__av--correct">Pub</span>}
                                                        {a.canPublish === false && <span className="qft__av qft__av--wrong">¬Pub</span>}
                                                        {a.shouldDelete === true && <span className="qft__av qft__av--delete">Del</span>}
                                                        {a.shouldRedo && a.shouldRedo !== 'nao' && <span className="qft__av qft__av--warn">Refazer</span>}
                                                    </div>
                                                    {collapsed
                                                        ? <ChevronDown size={14} className="qft__analysis-chevron" />
                                                        : <ChevronUp size={14} className="qft__analysis-chevron" />
                                                    }
                                                </button>
                                                {!collapsed && (
                                                    <div className="qft__analysis-item-body">
                                                        {a.isCorrect !== null && (
                                                            <div className="qft__analysis-field">
                                                                <span className="qft__analysis-field-lbl">Correta?</span>
                                                                <span className={`qft__av ${a.isCorrect ? 'qft__av--correct' : 'qft__av--wrong'}`}>
                                                                    {a.isCorrect ? '✓ Sim' : '✗ Não'}
                                                                </span>
                                                            </div>
                                                        )}
                                                        {a.canPublish !== null && (
                                                            <div className="qft__analysis-field">
                                                                <span className="qft__analysis-field-lbl">Publicar?</span>
                                                                <span className={`qft__av ${a.canPublish ? 'qft__av--correct' : 'qft__av--wrong'}`}>
                                                                    {a.canPublish ? '✓ Sim' : '✗ Não'}
                                                                </span>
                                                            </div>
                                                        )}
                                                        {a.shouldDelete !== null && (
                                                            <div className="qft__analysis-field">
                                                                <span className="qft__analysis-field-lbl">Excluir?</span>
                                                                <span className={`qft__av ${a.shouldDelete ? 'qft__av--delete' : 'qft__av--correct'}`}>
                                                                    {a.shouldDelete ? 'Sim' : 'Não'}
                                                                </span>
                                                            </div>
                                                        )}
                                                        {a.shouldRedo && (
                                                            <div className="qft__analysis-field">
                                                                <span className="qft__analysis-field-lbl">Refazer?</span>
                                                                <span className={`qft__av ${a.shouldRedo === 'totalmente' ? 'qft__av--delete' : a.shouldRedo === 'parcialmente' ? 'qft__av--warn' : 'qft__av--correct'}`}>
                                                                    {a.shouldRedo === 'totalmente' ? 'Totalmente' : a.shouldRedo === 'parcialmente' ? 'Parcialmente' : 'Não'}
                                                                </span>
                                                            </div>
                                                        )}
                                                        {a.suggestedDifficulty && (
                                                            <div className="qft__analysis-field">
                                                                <span className="qft__analysis-field-lbl">Dificuldade</span>
                                                                <span className={`qft__av qft__av--${a.suggestedDifficulty}`}>
                                                                    {a.suggestedDifficulty === 'facil' ? 'Fácil' : a.suggestedDifficulty === 'medio' ? 'Médio' : 'Difícil'}
                                                                </span>
                                                            </div>
                                                        )}
                                                        {a.improvementText && (
                                                            <div className="qft__analysis-obs">{a.improvementText}</div>
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            </>
                        )}
                    </section>
                </div>
            )}

            {showPasteModal && (
                <PasteAlternativesModal
                    onConfirm={handlePasteAlternatives}
                    onClose={() => setShowPasteModal(false)}
                />
            )}

            {showCreateCategory && (
                <div className="qft__overlay" onClick={() => setShowCreateCategory(false)}>
                    <div className="qft__create-cat-modal" onClick={e => e.stopPropagation()}>
                        <div className="qft__create-cat-header">
                            <span>Nova Categoria</span>
                            <button onClick={() => setShowCreateCategory(false)}><X size={14} /></button>
                        </div>
                        <div className="qft__create-cat-body">
                            <div className="qft__field">
                                <label className="qft__lbl">Nome</label>
                                <input
                                    className="qft__input"
                                    placeholder="ex: Cardiologia"
                                    value={newCatName}
                                    onChange={e => setNewCatName(e.target.value)}
                                    onKeyDown={e => e.key === 'Enter' && !creatingCat && handleInlineCreateCategory()}
                                    autoFocus
                                />
                            </div>
                            <div className="qft__field">
                                <label className="qft__lbl">Nível</label>
                                <div className="qft__sel-wrap">
                                    <select
                                        className="qft__select"
                                        value={newCatLevel}
                                        onChange={e => { setNewCatLevel(Number(e.target.value)); setNewCatParentId(''); }}
                                    >
                                        <option value={1}>Nível 1 — Campo de Estudo</option>
                                        <option value={2}>Nível 2 — Assunto</option>
                                        <option value={3}>Nível 3 — Tema</option>
                                        <option value={4}>Nível 4 — Subtema</option>
                                    </select>
                                    <ChevronDown size={11} className="qft__sel-icon" />
                                </div>
                            </div>
                            {newCatLevel > 1 && (
                                <div className="qft__field">
                                    <label className="qft__lbl">
                                        Pertence a ({['', 'Campo de Estudo', 'Assunto', 'Tema'][newCatLevel - 1]})
                                    </label>
                                    <div className="qft__sel-wrap">
                                        <select
                                            className={`qft__select${newCatParentId ? ' qft__select--on' : ''}`}
                                            value={newCatParentId}
                                            onChange={e => setNewCatParentId(e.target.value)}
                                        >
                                            <option value="">Selecione...</option>
                                            {allCategories
                                                .filter(c => c.level === newCatLevel - 1)
                                                .map(c => (
                                                    <option key={c._id} value={c._id}>
                                                        {getChain(c._id, allCategories)
                                                            .map(id => allCategories.find(x => x._id === id)?.name ?? '')
                                                            .join(' › ')}
                                                    </option>
                                                ))}
                                        </select>
                                        <ChevronDown size={11} className="qft__sel-icon" />
                                    </div>
                                </div>
                            )}
                        </div>
                        <div className="qft__create-cat-footer">
                            <button className="qft__edit-cancel" onClick={() => setShowCreateCategory(false)} disabled={creatingCat}>
                                <X size={12} /> Cancelar
                            </button>
                            <button
                                className="qft__add-cat"
                                onClick={handleInlineCreateCategory}
                                disabled={creatingCat || !newCatName.trim() || (newCatLevel > 1 && !newCatParentId)}
                            >
                                {creatingCat ? <Loader2 size={13} className="qft__spin" /> : <Plus size={13} />}
                                Criar
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default QuestionFormTab;
